import React, { forwardRef, useState, useEffect, useRef, useCallback, useImperativeHandle } from 'react';
import { Button, Input, FormGroup, Label } from 'reactstrap';
import { AudioRecorder } from '../../../AudioRecorder/AudioRecorder';
import SpellCheckSuggestions from '../SpellCheckSuggestions';
import FontSettings from '../../../FontSettings/FontSettings';
import './TextEditor.css';

export const TextEditor = forwardRef(({
    initialText,
    onSpellCheck,
    onTextChange,
    showToolbar = true,
    showAudioRecorder = true,
    fontSize,
    setFontSize,
    lineHeight,
    setLineHeight,
}, ref) => {
    const [spellCheckState, setSpellCheckState] = useState({
        showSuggestions: false,
        errorData: null,
        position: { left: 0, top: 0 },
        targetElement: null
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [replaceQuery, setReplaceQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
    const [options, setOptions] = useState({
        caseSensitive: false,
        wholeWord: false,
        regex: false
    });
    const [isLocked, setIsLocked] = useState(false);

    const editorRef = useRef(null);
    const lastSelection = useRef(null);
    const editorContainerRef = useRef(null);
    const searchInputRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const isComposingRef = useRef(false);
    const isTypingRef = useRef(false);

    const getCurrentText = useCallback(() => {
        return editorRef.current ? editorRef.current.textContent : '';
    }, []);

    const restoreCursorByOffset = useCallback((offset) => {
        if (!editorRef.current) return;

        const selection = window.getSelection();
        const range = document.createRange();

        let currentOffset = 0;
        const walker = document.createTreeWalker(
            editorRef.current,
            NodeFilter.SHOW_TEXT,
            null
        );

        let node;
        while ((node = walker.nextNode())) {
            if (offset <= currentOffset + node.length) {
                range.setStart(node, offset - currentOffset);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
                return;
            }
            currentOffset += node.length;
        }

        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
    }, []);

    const createSearchRegex = useCallback(() => {
        const escaped = escapeRegExp(searchQuery);
        if (options.wholeWord) {
            return new RegExp(
                `(^|[^\\p{L}\\d-])${escaped}(?=[^\\p{L}\\d-]|$)`,
                options.caseSensitive ? 'gu' : 'giu'
            );
        }
        return new RegExp(
            escaped,
            options.caseSensitive ? 'g' : 'gi'
        );
    }, [searchQuery, options]);

    const highlightSearchResults = useCallback((searchQuery, options, currentMatchIndex = -1) => {
        if (!editorRef.current) return;

        const editor = editorRef.current;
        const hadFocus = document.activeElement === editor;
        const selection = window.getSelection();
        const range = selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null;

        const cursorOffset = range ? getCursorOffset(editor, range) : 0;

        const marks = editor.querySelectorAll('mark.search-result');
        marks.forEach(mark => {
            const parent = mark.parentNode;
            if (parent) {
                parent.replaceChild(document.createTextNode(mark.textContent), mark);
                parent.normalize();
            }
        });

        if (!searchQuery) {
            if (hadFocus && range) {
                editor.focus();
                selection.removeAllRanges();
                selection.addRange(range);
            }
            return;
        }

        let regex;
        try {
            regex = createSearchRegex();
        } catch (e) {
            console.error("Invalid regex:", e);
            if (hadFocus && range) {
                editor.focus();
                selection.removeAllRanges();
                selection.addRange(range);
            }
            return;
        }

        let lastIndex = 0;
        let match;
        let matchCount = 0;
        const matches = [];

        const fragment = document.createDocumentFragment();
        const walker = document.createTreeWalker(
            editor,
            NodeFilter.SHOW_TEXT,
            null,
            false
        );

        let node;
        while ((node = walker.nextNode()) !== null) {
            const nodeText = node.nodeValue;
            let nodeLastIndex = 0;

            while ((match = regex.exec(nodeText)) !== null) {
                const matchStart = options.wholeWord ?
                    match.index + (match[1] ? match[1].length : 0) :
                    match.index;

                const matchEnd = options.wholeWord ?
                    matchStart + searchQuery.length :
                    match.index + match[0].length;

                matches.push({
                    start: lastIndex + matchStart,
                    end: lastIndex + matchEnd
                });

                if (matchStart > nodeLastIndex) {
                    fragment.appendChild(document.createTextNode(
                        nodeText.slice(nodeLastIndex, matchStart)
                    ));
                }

                const mark = document.createElement('mark');
                mark.className = `search-result ${matchCount === currentMatchIndex ? 'current-search-match' : ''}`;
                mark.textContent = nodeText.slice(matchStart, matchEnd);
                fragment.appendChild(mark);

                nodeLastIndex = matchEnd;
                matchCount++;

                if (match.index === regex.lastIndex) {
                    regex.lastIndex++;
                }
            }

            if (nodeLastIndex < nodeText.length) {
                fragment.appendChild(document.createTextNode(
                    nodeText.slice(nodeLastIndex)
                ));
            }

            lastIndex += nodeText.length;
        }

        editor.innerHTML = '';
        editor.appendChild(fragment);

        setSearchResults(matches);

        if (matches.length > 0 && currentMatchIndex === -1 && !isTypingRef.current) {
            setCurrentMatchIndex(0);
        }

        if (hadFocus) {
            editor.focus();
            restoreCursorPosition(editor, cursorOffset);
        }
    }, [createSearchRegex]);

    const performSearch = useCallback(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            if (!editorRef.current) return;

            if (!searchQuery.trim()) {
                setSearchResults([]);
                setCurrentMatchIndex(-1);
                highlightSearchResults('', options);
                return;
            }

            highlightSearchResults(searchQuery, options, currentMatchIndex);
            isTypingRef.current = false;
        }, 300);
    }, [searchQuery, options, currentMatchIndex, highlightSearchResults]);

    const handleEditorChange = useCallback(() => {
        if (!editorRef.current || isComposingRef.current) return;

        const newText = editorRef.current.textContent;
        if (onTextChange) {
            onTextChange(newText !== initialText);
        }

        isTypingRef.current = true;
        performSearch();
    }, [initialText, onTextChange, performSearch]);

    const setText = useCallback((text) => {
        if (editorRef.current) {
            editorRef.current.textContent = text;
            handleEditorChange();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getCursorPosition = useCallback(() => {
        const selection = window.getSelection();
        if (selection.rangeCount === 0 || !editorRef.current) return null;

        const range = selection.getRangeAt(0);
        const preCursorRange = range.cloneRange();
        preCursorRange.selectNodeContents(editorRef.current);
        preCursorRange.setEnd(range.endContainer, range.endOffset);

        return {
            offset: preCursorRange.toString().length,
            startContainer: range.startContainer,
            startOffset: range.startOffset,
            endContainer: range.endContainer,
            endOffset: range.endOffset
        };
    }, []);

    const setCursorPosition = useCallback((position) => {
        if (!position || !editorRef.current) return;

        const selection = window.getSelection();
        const range = document.createRange();

        try {
            range.setStart(position.startContainer, position.startOffset);
            range.setEnd(position.endContainer, position.endOffset);
            selection.removeAllRanges();
            selection.addRange(range);
        } catch (e) {
            range.selectNodeContents(editorRef.current);
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    }, []);

    const insertTextAtCursor = useCallback((text, cursorPosition = null) => {
        if (!editorRef.current) return;

        const currentText = getCurrentText();
        let newText;
        let newCursorPosition;

        if (cursorPosition) {
            newText = currentText.slice(0, cursorPosition.offset) +
                text +
                currentText.slice(cursorPosition.offset);

            newCursorPosition = {
                offset: cursorPosition.offset + text.length,
                startContainer: null,
                startOffset: 0,
                endContainer: null,
                endOffset: 0
            };
        } else {
            const currentPos = getCursorPosition();
            if (currentPos) {
                newText = currentText.slice(0, currentPos.offset) +
                    text +
                    currentText.slice(currentPos.offset);

                newCursorPosition = {
                    offset: currentPos.offset + text.length,
                    startContainer: currentPos.startContainer,
                    startOffset: currentPos.startOffset,
                    endContainer: currentPos.endContainer,
                    endOffset: currentPos.endOffset
                };
            } else {
                newText = currentText + text;
                newCursorPosition = {
                    offset: newText.length,
                    startContainer: null,
                    startOffset: 0,
                    endContainer: null,
                    endOffset: 0
                };
            }
        }

        setText(newText);

        performSearch();

        setTimeout(() => {
            if (newCursorPosition.startContainer && document.body.contains(newCursorPosition.startContainer)) {
                setCursorPosition(newCursorPosition);
            } else {
                restoreCursorByOffset(newCursorPosition.offset);
            }
        }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getCurrentText, getCursorPosition, setCursorPosition, setText, performSearch]);

    const lockEditor = useCallback(() => {
        setIsLocked(true);
        if (editorRef.current) {
            editorRef.current.setAttribute('contenteditable', 'false');
            editorRef.current.style.opacity = '0.7';
            editorRef.current.style.cursor = 'not-allowed';
        }
    }, []);

    const unlockEditor = useCallback(() => {
        setIsLocked(false);
        if (editorRef.current) {
            editorRef.current.setAttribute('contenteditable', 'true');
            editorRef.current.style.opacity = '';
            editorRef.current.style.cursor = '';
        }
    }, []);

    const handleAudioTextChange = useCallback((event) => {
        const { value } = event.target;

        setText(value);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        highlightSearchResults(searchQuery, options, currentMatchIndex);

        if (onTextChange) {
            onTextChange(value !== initialText);
        }
    }, [initialText, onTextChange, setText, searchQuery, options, currentMatchIndex, highlightSearchResults]);

    useEffect(() => {
        if (editorRef.current) {
            editorRef.current.textContent = initialText;
        }
    }, [initialText]);

    useImperativeHandle(ref, () => ({
        getCurrentText,
        setText,
        insertTextAtCursor,
        getCursorPosition,
        setCursorPosition,
        lockEditor,
        unlockEditor
    }));

    const getCursorOffset = (editor, range) => {
        const preCursorRange = range.cloneRange();
        preCursorRange.selectNodeContents(editor);
        preCursorRange.setEnd(range.endContainer, range.endOffset);
        return preCursorRange.toString().length;
    };

    const escapeRegExp = (string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };



    const restoreCursorPosition = (editor, offset) => {
        const selection = window.getSelection();
        const range = document.createRange();

        let currentOffset = 0;
        const walker = document.createTreeWalker(
            editor,
            NodeFilter.SHOW_TEXT,
            null
        );

        let node;
        while ((node = walker.nextNode())) {
            if (offset <= currentOffset + node.length) {
                range.setStart(node, offset - currentOffset);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
                return;
            }
            currentOffset += node.length;
        }

        range.selectNodeContents(editor);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
    };



    useEffect(() => {
        performSearch();
    }, [searchQuery, options, performSearch]);

    const saveSelection = () => {
        const selection = window.getSelection();
        if (selection.rangeCount > 0 && editorRef.current?.contains(selection.anchorNode)) {
            const range = selection.getRangeAt(0);
            lastSelection.current = {
                startContainer: range.startContainer,
                startOffset: range.startOffset,
                endContainer: range.endContainer,
                endOffset: range.endOffset
            };
        }
    };

    const restoreSelection = () => {
        if (!lastSelection.current || !editorRef.current) return;

        const selection = window.getSelection();
        const range = document.createRange();

        try {
            const { startContainer, startOffset, endContainer, endOffset } = lastSelection.current;
            range.setStart(startContainer, startOffset);
            range.setEnd(endContainer, endOffset);
            selection.removeAllRanges();
            selection.addRange(range);
        } catch (e) {
            range.selectNodeContents(editorRef.current);
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    };

    const handleReplace = () => {
        if (currentMatchIndex === -1) return;

        saveSelection();

        const editor = editorRef.current;
        const text = editor.textContent;
        const match = searchResults[currentMatchIndex];

        if (match) {
            const newText = text.slice(0, match.start) + replaceQuery + text.slice(match.end);
            editor.textContent = newText;
            handleEditorChange();

            performSearch();
        }

        restoreSelection();
    };

    const handleReplaceAll = () => {
        if (searchResults.length === 0) return;

        saveSelection();

        const editor = editorRef.current;
        let text = editor.textContent;
        let offset = 0;

        const sortedResults = [...searchResults].sort((a, b) => a.start - b.start);

        for (const result of sortedResults) {
            const adjustedStart = result.start + offset;
            const adjustedEnd = result.end + offset;

            text = text.slice(0, adjustedStart) + replaceQuery + text.slice(adjustedEnd);

            offset += replaceQuery.length - (result.end - result.start);
        }

        editor.textContent = text;
        handleEditorChange();

        performSearch();

        restoreSelection();
    };

    const navigateMatch = (direction) => {
        if (searchResults.length === 0) return;

        let newIndex;
        if (direction === 'next') {
            newIndex = currentMatchIndex + 1;
            if (newIndex >= searchResults.length) {
                newIndex = 0;
            }
        } else {
            newIndex = currentMatchIndex - 1;
            if (newIndex < 0) {
                newIndex = searchResults.length - 1;
            }
        }

        setCurrentMatchIndex(newIndex);
        highlightSearchResults(searchQuery, options, newIndex);
    };

    const handleOptionChange = (option) => {
        const newOptions = {
            ...options,
            [option]: !options[option]
        };
        setOptions(newOptions);
    };

    const handleEditorClick = (e) => {
        if (e.target.classList.contains('spelling-error')) {
            const errorData = JSON.parse(decodeURIComponent(e.target.dataset.error));
            const rect = e.target.getBoundingClientRect();

            setSpellCheckState({
                showSuggestions: true,
                errorData,
                position: {
                    left: Math.min(rect.left, window.innerWidth - 250),
                    top: rect.bottom + window.scrollY
                },
                targetElement: e.target
            });
        } else if (spellCheckState.showSuggestions) {
            setSpellCheckState(prev => ({ ...prev, showSuggestions: false }));
        }
    };

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            const selection = window.getSelection();
            if (selection.rangeCount > 0 && editorRef.current) {
                const range = selection.getRangeAt(0);
                range.deleteContents();
                const textNode = document.createTextNode('\n');
                range.insertNode(textNode);
                // Сдвигаем курсор после вставленного \n
                range.setStartAfter(textNode);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
                handleEditorChange();
            }
        }
    }, [handleEditorChange]);

    const handlePaste = useCallback((e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            range.insertNode(document.createTextNode(text));
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
            handleEditorChange();
        }
    }, [handleEditorChange]);

    const handleSelectSuggestion = (suggestion) => {
        if (!spellCheckState.targetElement) return;

        const errorData = spellCheckState.errorData;
        const editor = editorRef.current;
        const text = editor.textContent;
        const newText = text.slice(0, errorData.pos) + suggestion + text.slice(errorData.pos + errorData.len);

        editor.textContent = newText;
        setSpellCheckState(prev => ({ ...prev, showSuggestions: false }));

        if (onSpellCheck) {
            onSpellCheck(newText);
        }
    };

    const handleIgnoreSpelling = () => {
        if (!spellCheckState.targetElement) return;

        spellCheckState.targetElement.classList.remove(
            'spelling-error-ru',
            'spelling-error-en'
        );
        spellCheckState.targetElement.classList.add('spelling-ignored');

        setSpellCheckState(prev => ({ ...prev, showSuggestions: false }));
    };

    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    const handleCompositionStart = () => {
        isComposingRef.current = true;
    };

    const handleCompositionEnd = () => {
        isComposingRef.current = false;
        handleEditorChange();
    };

    return (
        <div className="text-edit-container" ref={editorContainerRef}>
            {showToolbar && (
                <div className="editor-toolbar">
                    <div className="find-replace-toolbar">
                        <div className="find-replace-controls btn-spacing">
                            <div className="search-inputs btn-spacing">
                                <Input
                                    innerRef={searchInputRef}
                                    className="text-input"
                                    type="text"
                                    placeholder="Найти"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <Input
                                    className="text-input"
                                    type="text"
                                    placeholder="Заменить на"
                                    value={replaceQuery}
                                    onChange={(e) => setReplaceQuery(e.target.value)}
                                />
                            </div>
                            <div className="replace-btns btn-spacing">
                                <Button
                                    className="dt1520-button active-dt1520-button view-button blue-button-with-borders"
                                    outline
                                    onClick={handleReplace}
                                    disabled={searchResults.length === 0}>
                                    Заменить
                                </Button>

                                <Button
                                    className="dt1520-button active-dt1520-button view-button blue-button-with-borders"
                                    outline
                                    onClick={handleReplaceAll}
                                    disabled={searchResults.length === 0}>
                                    Заменить всё
                                </Button>
                            </div>
                        </div>
                        <div className="edit-search-controls mt-2 btn-spacing">
                            <div className="search-options">
                                <FormGroup check>
                                    <Label check>
                                        <Input
                                            type="checkbox"
                                            id="caseSensitive"
                                            checked={options.caseSensitive}
                                            onChange={() => handleOptionChange('caseSensitive')}
                                        />
                                        Учитывать регистр
                                    </Label>
                                </FormGroup>
                                <FormGroup check>
                                    <Label check>
                                        <Input
                                            type="checkbox"
                                            id="wholeWord"
                                            checked={options.wholeWord}
                                            onChange={() => handleOptionChange('wholeWord')}
                                        />
                                        Только целые слова
                                    </Label>
                                </FormGroup>
                            </div>
                            <div className="nav-search-controls">
                                <Button
                                    className="nav-btn"
                                    outline
                                    onClick={() => navigateMatch('prev')}
                                    disabled={searchResults.length === 0}
                                >
                                    &larr;
                                </Button>

                                <span className="match-counter">
                                    {searchResults.length > 0
                                        ? `${currentMatchIndex + 1}/${searchResults.length}`
                                        : '0/0'}
                                </span>

                                <Button
                                    className="nav-btn"
                                    outline
                                    onClick={() => navigateMatch('next')}
                                    disabled={searchResults.length === 0}
                                >
                                    &rarr;
                                </Button>
                            </div>
                        </div>
                    </div>
                    {showAudioRecorder && (
                        <div className="audio-recorder-toolbar">
                            <AudioRecorder
                                onMessageChange={handleAudioTextChange}
                                onTextInserted={performSearch}
                                messageMaxLength={10000}
                                isLoading={isLocked}
                                targetFieldClass="text-editor"
                                defaultTargetFieldId="text-editor"
                                recordingBtnText="Записать изменения"
                            />
                        </div>
                    )}

                    <FontSettings
                        fontSize={fontSize}
                        setFontSize={setFontSize}
                        lineHeight={lineHeight}
                        setLineHeight={setLineHeight}
                    />
                </div>
            )}

            <div
                className="editor-wrapper"
                onClick={handleEditorClick}
            >
                <div
                    id="text-editor"
                    className="text-editor"
                    contentEditable={!isLocked}
                    onInput={handleEditorChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                    ref={editorRef}
                    style={{
                        opacity: isLocked ? 0.7 : 1,
                        cursor: isLocked ? 'not-allowed' : 'text',
                        fontSize: fontSize ? `${fontSize}px` : undefined,
                        lineHeight: lineHeight ? lineHeight : undefined,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                    }}
                />
            </div>

            {spellCheckState.showSuggestions && (
                <SpellCheckSuggestions
                    errorData={spellCheckState.errorData}
                    position={spellCheckState.position}
                    onSelectSuggestion={handleSelectSuggestion}
                    onIgnore={handleIgnoreSpelling}
                />
            )}
        </div>
    );
});